import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NotificationService, UploadService, AmplitudeService } from '@core/services';
import { interval, Subscription } from 'rxjs';
import { LoadingService } from '@core/component/global-loading/loading.service';

interface FileMetadata {
  id?: string;
  name: string;
  storagePath: string;
  note: string;
  size?: number;
  type?: string;
  status?: string;
  error_message?: string;
  isUploading?: boolean;
  uploadError?: string;
  originalFile?: File;
  originalNote?: string;
}

@Component({
  selector: 'app-upload',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule],
  templateUrl: './upload.component.html',
  styleUrl: './upload.component.scss',
})
export class UploadComponent implements OnInit, OnDestroy {
  private statusPolling?: Subscription;
  private readonly statusPollInterval = 5000;
  uploadedFiles: FileMetadata[] = [];
  showDeleteConfirmation = false;
  fileToDelete: FileMetadata | null = null;
  currentFile: File | null = null;
  metadataForm: FormGroup;
  isUploading = false;
  pendingUploads: FileMetadata[] = [];

  constructor(
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private uploadService: UploadService,
    private _amplitudeService: AmplitudeService,
    private loadingService: LoadingService
  ) {
    this.metadataForm = this.fb.group({
      name: ['', [Validators.required]],
      note: ['', [Validators.required, Validators.maxLength(200)]],
    });
  }

  async ngOnInit(): Promise<void> {
    if (this.isUserAuthenticated()) {
      await this.loadFiles({ showLoader: true });
    }
  }

  ngOnDestroy() {
    this.statusPolling?.unsubscribe();
  }

  private async loadFiles(options: { showLoader?: boolean } = {}): Promise<void> {
    if (!this.isUserAuthenticated()) {
      return;
    }

    const { showLoader = false } = options;

    if (showLoader) {
      this.loadingService.show();
    }

    try {
      const files = await this.uploadService.getFiles();
      this.uploadedFiles = files.map(file => ({
        id: file.id,
        name: this.getDisplayName(file.path),
        storagePath: file.path,
        note: '',
        size: file.size,
        type: file.type,
        status: file.status,
        error_message: file.error_message
      }));
    } catch (error: any) {
      console.error('Error loading files:', error);
      if (error.message?.includes('No active session')) {
        console.warn('Session expired, user needs to re-authenticate');
      } else {
        this.notificationService.notify('Error loading files');
      }
    } finally {
      if (showLoader) {
        this.loadingService.hide();
      }
      this.syncStatusPolling();
    }
  }

  private getDisplayName(path: string): string {
    const filename = path.split('/').pop() || '';
    return filename.replace(/^\d+-/, '');
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.handleFileSelection(files[0]);
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.handleFileSelection(input.files[0]);
    }
  }

  handleFileSelection(file: File): void {
    this._amplitudeService.trackEvent('Upload_AddFile', {
      file_name: file.name,
      file_type: file.type || file.name.split('.').pop() || 'unknown',
      size: this.formatFileSize(file.size),
      user_id: this.getUserId()
    });

    const displayName = this.getDisplayName(file.name);
    const defaultNote = 'Uploaded via quick upload';
    const sizeLabel = this.formatFileSize(file.size || 0);

    this.currentFile = file;
    this.metadataForm.setValue({
      name: displayName,
      note: defaultNote,
    });

    const optimisticEntry: FileMetadata = {
      name: displayName,
      storagePath: '',
      note: `Uploading... (${sizeLabel})`,
      size: file.size,
      type: file.type,
      status: 'uploading',
      isUploading: true,
      originalFile: file,
      originalNote: defaultNote
    };

  this.pendingUploads = [optimisticEntry, ...this.pendingUploads];
  this.syncStatusPolling();
  void this.saveMetadata();
  }

  async saveMetadata(): Promise<void> {
    if (!this.metadataForm.valid || !this.currentFile || !this.isUserAuthenticated()) return;
    
    this.isUploading = true;
    try {
  const { name, note } = this.metadataForm.value;
      const uploadingFile = this.currentFile;
      await this.uploadService.uploadFile(uploadingFile, { name, note });
      await this.loadFiles();
      this.resetUploadForm();

      // Track save event
      this._amplitudeService.trackEvent('Upload_Save', {
        status: 'success',
        file_count: this.uploadedFiles.length,
        user_id: this.getUserId()
      });
      this.notificationService.notify('File uploaded successfully');
  this.pendingUploads = this.pendingUploads.filter(item => item.originalFile !== uploadingFile);
      this.syncStatusPolling();
    } catch (error: any) {
      console.error('Upload error:', error);
      this.notificationService.notify(error.message || 'Error uploading file');
      if (this.currentFile) {
        this.pendingUploads = this.pendingUploads.map(item =>
          item.originalFile === this.currentFile
            ? {
                ...item,
                status: 'error',
                uploadError: error.message || 'Upload failed',
                isUploading: false,
                note: 'Upload failed. Try again.'
              }
            : item
        );
      }
      this.syncStatusPolling();
    } finally {
      this.isUploading = false;
    }
  }

  private resetUploadForm(): void {
    this.currentFile = null;
    this.metadataForm.reset();
  }

  async deleteFile(): Promise<void> {
    if (this.fileToDelete?.id) {
      try {
        // Track delete event
        this._amplitudeService.trackEvent('Upload_Delete', {
          file_name: this.fileToDelete.name,
          user_id: this.getUserId()
        });

        await this.uploadService.deleteFile(this.fileToDelete.id);
        await this.loadFiles();
        this.notificationService.notify('File deleted successfully');
      } catch (error: any) {
        console.error('Delete error:', error);
        this.notificationService.notify(error.message || 'Error deleting file');
      } finally {
        this.cancelDelete();
      }
    }
  }

  confirmDelete(file: FileMetadata): void {
    this.fileToDelete = file;
    this.showDeleteConfirmation = true;
  }

  cancelDelete(): void {
    this.fileToDelete = null;
    this.showDeleteConfirmation = false;
  }

  async downloadFile(file: FileMetadata): Promise<void> {
    try {
      const blob = await this.uploadService.downloadFile(file.id!);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      this.notificationService.notify('File downloaded successfully');
    } catch (error: any) {
      console.error('Download error:', error);
      this.notificationService.notify(error.message || 'Error downloading file');
    }
  }

  private isUserAuthenticated(): boolean {
    const userInfo = sessionStorage.getItem('userInfo');
    if (!userInfo) return false;
    
    try {
      const parsedUserInfo = JSON.parse(userInfo);
      return !!(parsedUserInfo.access_token && parsedUserInfo.user);
    } catch (error) {
      return false;
    }
  }

  private getUserId(): string {
    const userInfo = sessionStorage.getItem('userInfo');
    if (userInfo) {
      try {
        const parsedUserInfo = JSON.parse(userInfo);
        return parsedUserInfo.user?.id || parsedUserInfo.id || 'anonymous';
      } catch (error) {
        return 'anonymous';
      }
    }
    return 'anonymous';
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + sizes[i];
  }

  getStatusClass(status: string): string {
    if (status?.toLowerCase().includes('error') || status?.toLowerCase().includes('failed')) {
      return 'status-failed';
    }
    switch (status?.toLowerCase()) {
      case 'uploaded': return 'status-pending';
      case 'processing': return 'status-processing';
      case 'successfully processed': return 'status-completed';
      case 'completed': return 'status-completed';
      default: return 'status-pending';
    }
  }

  private syncStatusPolling(): void {
    const hasPending =
      this.uploadedFiles.some(file => this.isPendingStatus(file.status)) ||
      this.pendingUploads.some(file => this.isPendingStatus(file.status));

    if (hasPending) {
      if (!this.statusPolling || this.statusPolling.closed) {
        this.statusPolling = interval(this.statusPollInterval).subscribe(() => {
          if (this.isUserAuthenticated()) {
            void this.loadFiles();
          }
        });
      }
    } else if (this.statusPolling) {
      this.statusPolling.unsubscribe();
      this.statusPolling = undefined;
    }
  }

  private isPendingStatus(status?: string): boolean {
    if (!status) {
      return true;
    }

    const normalized = status.toLowerCase();
    if (normalized.includes('error') || normalized.includes('failed')) {
      return false;
    }

    return !['completed', 'successfully processed'].includes(normalized);
  }

  retryPendingUpload(pending: FileMetadata): void {
    if (!pending.originalFile) {
      return;
    }

    const defaultNote = pending.originalNote || 'Uploaded via quick upload';
    const sizeLabel = this.formatFileSize(pending.size || pending.originalFile?.size || 0);

    this._amplitudeService.trackEvent('Upload_Retry', {
      file_name: pending.name,
      user_id: this.getUserId()
    });

    this.pendingUploads = this.pendingUploads.map(item =>
      item === pending
        ? {
            ...item,
            status: 'uploading',
            note: `Retrying upload... (${sizeLabel})`,
            uploadError: undefined,
            isUploading: true
          }
        : item
    );

  this.currentFile = pending.originalFile;
  this.metadataForm.setValue({ name: pending.name, note: defaultNote });
  void this.saveMetadata();
    this.syncStatusPolling();
  }

  dismissPendingError(pending: FileMetadata): void {
    this._amplitudeService.trackEvent('Upload_DismissError', {
      file_name: pending.name,
      user_id: this.getUserId()
    });

    if (this.currentFile && pending.originalFile === this.currentFile) {
      this.resetUploadForm();
    }

    this.pendingUploads = this.pendingUploads.filter(item => item !== pending);
    this.syncStatusPolling();
  }
}
